import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ColumnConfig, ActionConfig } from './tabular-data.interfaces';
import { JOB_STATUS_ID } from '@dal/core';
import { AssetTypeId } from '../../enums/asset-type.enum';

@Component({
  selector: 'dal-shared-ui-tabular-data',
  templateUrl: './tabular-data.component.html',
  styleUrl: './tabular-data.component.scss',
})
export class TabularDataComponent {
  @Input() dataSource: Record<string, unknown>[] = [];
  @Input() title: string;
  @Input() displayedColumns: ColumnConfig[] = [];
  @Input() dialogLink = false;
  @Input() enableSorting = false;
  @Input() customActions: ActionConfig[] = [];
  sortOrder: string = '';
  sortColumn: string = '';

  // Make enums available to template
  readonly AssetTypeId = AssetTypeId;

  @Output() dialogOpened = new EventEmitter<Record<string, unknown>>();
  @Output() downloadReport = new EventEmitter<number[]>();
  @Output() actionClicked = new EventEmitter<{
    action: string;
    element: Record<string, unknown>;
  }>();
  @Output() sortChanged = new EventEmitter<{
    column: string;
    order: string;
  }>();

  constructor(
    private router: Router,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    iconRegistry.addSvgIcon(
      'arrow-repeat',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow-repeat.svg')
    );
    iconRegistry.addSvgIcon(
      'sort-alt',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/sort-alt.svg')
    );
    iconRegistry.addSvgIcon(
      'actions',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/actions.svg')
    );
    iconRegistry.addSvgIcon(
      'exclamation-square-fill',
      sanitizer.bypassSecurityTrustResourceUrl(
        '/assets/icons/exclamation-square-fill.svg'
      )
    );
  }

  toggleSort(order: string, column: string) {
    if (this.sortColumn === column && this.sortOrder === order) {
      // If the column is already sorted in this order, reset
      this.sortOrder = '';
      this.sortColumn = '';
      this.sortChanged.emit({ column: '', order: '' });
    } else {
      this.sortOrder = order;
      this.sortColumn = column;
      this.sortChanged.emit({ column, order });
    }
  }

  triggerDownload(element: Record<string, unknown>) {
    const tasks = element['tasks'] as { asset_id: number }[];
    this.downloadReport.emit(tasks.map((task) => task.asset_id));
  }

  navigate(element: Record<string, unknown>) {
    this.router.navigateByUrl(`/search?jobID=${element['job_id']}`);
  }

  openDialog(element: Record<string, unknown>) {
    this.dialogOpened.emit(element);
  }

  // Helper methods for column configuration
  get displayedColumnKeys(): string[] {
    return this.displayedColumns.map((col) => col.key);
  }

  getFilteredActions(element: Record<string, unknown>): ActionConfig[] {
    return this.customActions.filter(
      (action) => !action.condition || action.condition(element)
    );
  }

  onActionClick(action: string, element: Record<string, unknown>) {
    this.actionClicked.emit({ action, element });
  }

  isSuccessfulJob(element: Record<string, unknown>): boolean {
    return element['status_id'] === JOB_STATUS_ID.SUCCESS;
  }

  isNavigableJob(element: Record<string, unknown>): boolean {
    return (
      this.isSuccessfulJob(element) ||
      (element['status_id'] === JOB_STATUS_ID.FAILED &&
        this.isPartialFailure(element))
    );
  }

  getFailedTaskCount(element: Record<string, unknown>): number {
    const tasks = element['tasks'] as { status_id: number }[] | undefined;
    if (!tasks || tasks.length === 0) return 0;
    let count = 0;
    for (const task of tasks) {
      if (task.status_id === JOB_STATUS_ID.FAILED) {
        count++;
      }
    }
    return count;
  }

  isPartialFailure(element: Record<string, unknown>): boolean {
    const tasks = element['tasks'] as { status_id: number }[] | undefined;
    if (!tasks || tasks.length === 0) return false;
    let failed = 0;
    let successful = 0;
    for (const task of tasks) {
      if (task.status_id === JOB_STATUS_ID.FAILED) failed++;
      else if (task.status_id === JOB_STATUS_ID.SUCCESS) successful++;
    }
    return failed > 0 && successful > 0;
  }

  navigateIfNavigable(element: Record<string, unknown>): void {
    if (this.isNavigableJob(element)) {
      this.navigate(element);
    }
  }
}
